import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ExternalLink, ArrowLeft, Calendar, User, Share2, Clock, Sparkles } from 'lucide-react';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from '@clerk/clerk-react';
import { apiService } from '../services/api';
import PushButton from '../components/PushButton';
import { Coffee } from 'lucide-react';

const DEFAULT_THUMBNAIL = 'https://customer-assets.emergentagent.com/job_aidevnewshub/artifacts/yk3yvwx0_thumbnail.webp';

const categoryColorMap = {
  'AI News': 'bg-indigo-600',
  'Programming': 'bg-green-600',
  'Machine Learning': 'bg-emerald-600',
  'Research Papers': 'bg-rose-600',
  'Tools': 'bg-yellow-600',
  'Tutorials': 'bg-sky-600',
  'default': 'bg-slate-800/90'
};

const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const isPremium = 
    user?.publicMetadata?.subscriptionStatus === 'active' || 
    user?.publicMetadata?.subscription === 'premium' ||
    user?.publicMetadata?.plan === 'premium' ||
    user?.publicMetadata?.isPremium === true ||
    user?.unsafeMetadata?.subscriptionStatus === 'active' ||
    user?.unsafeMetadata?.subscription === 'premium' ||
    user?.unsafeMetadata?.plan === 'premium' ||
    user?.privateMetadata?.subscriptionStatus === 'active';

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await apiService.getArticleById(id);
        setArticle(data.article);
        
        // Update meta tags for SEO
        if (data.article) {
          // Update page title
          document.title = `${data.article.title} - AI News Hub`;
          
          // Update or create meta description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = data.article.excerpt;
          
          // Update Open Graph tags
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
          }
          ogTitle.content = data.article.title;
          
          let ogDescription = document.querySelector('meta[property="og:description"]');
          if (!ogDescription) {
            ogDescription = document.createElement('meta');
            ogDescription.setAttribute('property', 'og:description');
            document.head.appendChild(ogDescription);
          }
          ogDescription.content = data.article.excerpt;
          
          let ogImage = document.querySelector('meta[property="og:image"]');
          if (!ogImage) {
            ogImage = document.createElement('meta');
            ogImage.setAttribute('property', 'og:image');
            document.head.appendChild(ogImage);
          }
          ogImage.content = data.article.imageUrl || DEFAULT_THUMBNAIL;
          
          let ogUrl = document.querySelector('meta[property="og:url"]');
          if (!ogUrl) {
            ogUrl = document.createElement('meta');
            ogUrl.setAttribute('property', 'og:url');
            document.head.appendChild(ogUrl);
          }
          ogUrl.content = window.location.href;
          
          // Update Twitter Card tags
          let twitterCard = document.querySelector('meta[name="twitter:card"]');
          if (!twitterCard) {
            twitterCard = document.createElement('meta');
            twitterCard.name = 'twitter:card';
            document.head.appendChild(twitterCard);
          }
          twitterCard.content = 'summary_large_image';
          
          let twitterTitle = document.querySelector('meta[name="twitter:title"]');
          if (!twitterTitle) {
            twitterTitle = document.createElement('meta');
            twitterTitle.name = 'twitter:title';
            document.head.appendChild(twitterTitle);
          }
          twitterTitle.content = data.article.title;
          
          let twitterDescription = document.querySelector('meta[name="twitter:description"]');
          if (!twitterDescription) {
            twitterDescription = document.createElement('meta');
            twitterDescription.name = 'twitter:description';
            document.head.appendChild(twitterDescription);
          }
          twitterDescription.content = data.article.excerpt;
          
          let twitterImage = document.querySelector('meta[name="twitter:image"]');
          if (!twitterImage) {
            twitterImage = document.createElement('meta');
            twitterImage.name = 'twitter:image';
            document.head.appendChild(twitterImage);
          }
          twitterImage.content = data.article.imageUrl || DEFAULT_THUMBNAIL;
        }
        
        // Fetch related articles (same category)
        if (data.article?.category) {
          const relatedData = await apiService.getArticles(data.article.category, 4, 0);
          // Filter out current article
          const filtered = relatedData.articles.filter(a => a.id !== id);
          setRelatedArticles(filtered.slice(0, 3));
        }
      } catch (error) {
        console.error("Error fetching article", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    
    // Cleanup: restore default meta tags when component unmounts
    return () => {
      document.title = 'AI News Hub - Your daily dose of AI & Tech';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.content = 'Your trusted source for AI, programming and technology news. Stay updated with the latest developments in artificial intelligence, machine learning, and tech innovations.';
      }
    };
  }, [id]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-slate-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-slate-800 mb-4">Article not found</h1>
          <p className="text-slate-600 mb-8">The article you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')} className="bg-indigo-600 hover:bg-indigo-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                  AI News Hub
                </div>
                <p className="text-xs text-slate-600">Your daily dose of AI & Tech</p>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center">
                <div className="mr-2">
                  <PushButton />
                </div>
                <div className="mr-3">
                  <Link to="/hub" className="px-3 py-2 rounded-md text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700">
                    Hub
                  </Link>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/hub" className="md:hidden px-2 py-1 rounded text-sm text-slate-700 hover:bg-slate-100 border border-slate-200">
                  Hub
                </Link>
                <SignedOut>
                  <SignInButton>
                    <button aria-label="Open login" className="p-2 rounded-full hover:bg-slate-100">
                      <User className="w-5 h-5 text-slate-700" />
                    </button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <UserButton />
                </SignedIn>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6 pl-0 hover:pl-2 transition-all text-slate-600 hover:text-indigo-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <article className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
          {/* Hero Image */}
          <div className="h-80 w-full relative overflow-hidden">
            <img 
              src={article.imageUrl || DEFAULT_THUMBNAIL} 
              alt={article.title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <Badge className={`absolute bottom-6 left-6 text-white border-0 px-4 py-2 text-base ${categoryColorMap[article.category] || categoryColorMap['default']}`}>
              {article.category}
            </Badge>
          </div>

          {/* Content */}
          <div className="p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-8 pb-6 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="font-medium text-slate-700">{article.source}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(article.publishedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <button 
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>

            {/* Article Summary/Excerpt */}
            <div className="prose prose-lg prose-slate max-w-none mb-10">
              <p className="text-xl text-slate-700 leading-relaxed font-medium mb-6">
                {article.excerpt}
              </p>
              
              {/* If you have full content, you can add it here */}
              {article.content && (
                <div className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.content }} />
              )}
            </div>

            {/* CTA to original article */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-8 rounded-2xl border border-indigo-100">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="font-bold text-xl text-slate-900 mb-2">Read the full article</h3>
                  <p className="text-slate-600">Get all the details and insights from the original source</p>
                </div>
                <Button 
                  size="lg" 
                  className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
                  onClick={() => window.open(article.articleUrl || article.sourceUrl, '_blank')}
                >
                  Read on {article.source} <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </article>

        {/* Ad Banner (if not premium) */}
        {!isPremium && (
          <div className="my-8">
            <Card className="border-transparent shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="rounded-lg p-[1px] bg-gradient-to-r from-blue-600 to-indigo-600">
                  <div className="min-h-28 w-full bg-[#0f172a] relative rounded-lg p-5 text-center">
                    {/* Blue Radial Glow Background */}
                    <div
                      className="absolute inset-0 z-0"
                      style={{
                        backgroundImage: `radial-gradient(circle 600px at 50% 50%, rgba(59,130,246,0.3), transparent)`,
                      }}
                    />
                    <div className="relative z-10 text-white">
                      <Coffee className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                      <p className="text-sm mb-3">If you want to enjoy all content without ads, subscribe to Premium — €1/month. Cheaper than a coffee!</p>
                      <a href="/suscription" className="inline-block mt-1 px-4 py-2 bg-amber-400 hover:bg-amber-500 rounded-md text-slate-900 font-semibold">Subscribe →</a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Card 
                  key={related.id}
                  className="group overflow-hidden border-slate-200 hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => navigate(`/article/${related.id}`)}
                >
                  <div className="relative overflow-hidden h-40">
                    <img 
                      src={related.imageUrl || DEFAULT_THUMBNAIL} 
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <Badge className={`absolute top-3 left-3 text-white border-0 text-xs ${categoryColorMap[related.category] || categoryColorMap['default']}`}>
                      {related.category}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h2 className="font-semibold text-base text-slate-800 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {related.title}
                    </h2>
                    <p className="text-xs text-slate-500 mt-2">{related.source}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-3">AI News Hub</h4>
              <p className="text-slate-400 text-sm">
                Your trusted source for AI, programming and technology news.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3">Resources</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="https://intheworldofai.com/" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">In The World of AI</a>
                <a href="https://www.hubdev.tools/category/ai" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">HubDev — AI</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3">Categories</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>AI News</p>
                <p>Programming</p>
                <p>Machine Learning</p>
                <p>Research Papers</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © 2024 AI News Hub. Automated collection of AI and programming news.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePage;
